import {ArgValidator} from 'neo-common';

export interface IClientHostSupportFlags {
    /**
     * flag which determine if client host supports paytable.
     */
    paytable: boolean;

    /**
     * flag which determine if client host supports get/set for volume.
     */
    volume: boolean;

    /**
     * Flag which determine if request for game configuration agains game server should be called which will lead to merge of local game configuration.
     */
    serverConfigurationRequestAllowed: boolean;

    /**
     * Flag which determine if client host suports get/set for denomination.
     */
    denomination: boolean;

    /**
     * Flag which determine if client host supports shared/game settings saved across restarts of client host.
     */
    settings: boolean;

    /**
     * Flag determine if sending and receiving from/to external system is supported or not.
     */
    externalMessage: boolean;
}

/**
 * Type for game actions.
 */
export type GameActions = {
    [action: string]: boolean;
};

/**
 * enum for actions group when dispatching action from client host to game.
 */
export enum ClientHostActionGroup {
    Default = <any>'default',
    Terminal = <any>'terminal'
}

export interface ClientHostDispatchExitRequest {
    /**
     * Active game should be able to finish game normally but does not allow to start new game
     */
    GameCollect: boolean;
}

/**
 * enum for paytable actions when dispatching from client host to paytable
 */
export enum ClientHostPaytableActions {
    LoadPaytable = <any>'LoadPaytable',
    ShowPaytable = <any>'ShowPaytable',
    PaytableLoaded = <any>'PaytableLoaded',
    PaytableAction = <any>'PaytableAction'
}

/**
 * enum for dialog type
 */
export enum ClientHostDialogType {
    Normal = <any>'Normal',
    Error = <any>'Error',
    Responsible1 = <any>'Responsible1'
}

/**
 * enum for client host metadata types
 */
export enum ClientHostMetadataTypes {
    BackgroundMusic,
    MuteVolumeAllowed
}

/**
 * data about volume settings.
 */
export interface ClientHostVolumeData {
    /**
     * @property CurrentStepNo - actual level of volume defined by step number.
     * Valid range is 0-3.
     */
    CurrentStepNo: number;
}

export interface ClientHostErrorData {
    /**
     * code of error (if any) which was the reason to end game.
     */
    errorCode?: string;

    /**
     * message title of error (if any) which was the reason to end game.
     */
    errorMessageTitle?: string;

    /**
     * message description of error (if any) which was the reason to end game.
     */
    errorMessageDescription?: string;

    /**
     * data of error (if any) which was the reason to end game.
     */
    errorData?: Object;
}

/**
 * data about show dialog request
 */
export interface ClientHostDialogRequest {
    /**
     * Text message to be displayed in dialog
     */
    text: string;

    /**
     * type of dialog
     */
    type: ClientHostDialogType;

    /**
     * Data associated with error (if any).
     */
    error: ClientHostErrorData;

    /**
     * Custom data object.
     */
    data?: Object;
}

export interface ClientHostResponsibleDialogData {
    /**
     * Flag indicating whenever 'exit' button should be visible
     */
    exit: boolean;

    /**
     * Flag indicating whenever 'history' button should be visible
     */
    history: boolean;

    /**
     * Flag indicating whenever 'continue' button should be visible
     */
    continue: boolean;

    /**
     * Date when responsible player timer started (should be used to count total time of playing game)
     */
    startTime: Date;

    /**
     * Interval (in seconds) of how ofter is reality check made.
     */
    interval: number;
}

/**
 * data about end game request
 */
export interface ClientHostEndGameRequest extends ClientHostErrorData {
    /**
     * reason about why game end is requested.
     */
    reason: string;
}

export interface ClientHostPaytableRequest {
    action: string;
}

export interface ClientHostPaytableLoadRequest extends ClientHostPaytableRequest {
    /**
     * @property url - url of paytable
     */
    url: string;
}

export interface ClientHostPaytableShowRequest extends ClientHostPaytableRequest {
    initData: Object;
}

export interface ClientHostPaytableActionRequest extends ClientHostPaytableRequest {
    type: string;
    value: string | Object;
}

/**
 * @interface ClientHostPaytableLoadedRequest
 * notify that paytable was loaded with 'loadSucceed' set to true on success or false on failure (send from paytable to client host)
 */
export interface ClientHostPaytableLoadedRequest extends ClientHostPaytableRequest {
    loadSucceed: boolean;
}

/**
 * @class ClientHostPaytableRequests - set of paytable requests types.
 */
export class ClientHostPaytableRequests {
    public static createLoadPaytable(url: string): ClientHostPaytableRequest {
        ArgValidator.ensureStringIsNotNullOrEmpty(url, 'url');

        var _action = ClientHostPaytableActions.LoadPaytable.toString();
        var result: ClientHostPaytableLoadRequest = {
            action: _action,
            url: url
        };
        return result;
    }

    public static createShowPaytable(initData: Object): ClientHostPaytableShowRequest {
        var _action = ClientHostPaytableActions.ShowPaytable.toString();
        var result: ClientHostPaytableShowRequest = {
            action: _action,
            initData: initData
        };
        return result;
    }

    public static createActionWithValue(type: string, value: string | Object): ClientHostPaytableRequest {
        var _action = ClientHostPaytableActions.PaytableAction.toString();
        var result: ClientHostPaytableActionRequest = {
            action: _action,
            type: type,
            value: value
        };
        return result;
    }

    public static createPaytableLoaded(loadSucceed: boolean): ClientHostPaytableRequest {
        var _action = ClientHostPaytableActions.PaytableLoaded.toString();
        var result: ClientHostPaytableLoadedRequest = {
            action: _action,
            loadSucceed: loadSucceed
        };

        return result;
    }
}

export interface IGameEventData {
    event: string;
}

export interface IDefaultGameEventData extends IGameEventData {
    customData?: Object;
    messageId: number;
    gameRoundId?: number;
    unpaidWin: number;
}

export enum OnOffState {
    On = <any>'on',
    Off = <any>'off',
}

export enum AutoplayAction {
    Stop = <any>'stop',
    Enable = <any>'enable',
    Disable = <any>'disable'
}

export enum GamePlayAction {
    Enable = <any>'enable',
    Disable = <any>'disable'
}



/** WEBPACK FOOTER **
 ** ./Engine/ClientHost/Types.ts
 **/